import {
  Scene,
  Group,
  MaterialLoader,
  ObjectLoader,
  Object3D,
  Mesh,
  Vector3,
  Material,
  DoubleSide,
  Color,
} from 'three'
import type { Object3DEventMap } from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import type { Configuration3d, Materials, Objects } from '~/types/configurator'
import { isSceneObject } from '~/types/configurator'
import { nanoid } from 'nanoid'

let scene: Scene
let chairModel = new Group()
const gltfLoader = new GLTFLoader()
const dracoLoader = new DRACOLoader()
dracoLoader.setDecoderPath('/draco/')
gltfLoader.setDRACOLoader(dracoLoader)
const materialLoader = new MaterialLoader()
const objectLoader = new ObjectLoader()

const materials: Record<string, string> = {}
const models: Record<
  string,
  {
    model: Group
    points: Object3D<Object3DEventMap> | undefined
    isBasePart: boolean
    name: string
    materialName: string
    groupId?: string
    isActive: boolean
  }
> = {}

export default function useWorld(_scene?: Scene) {
  const {
    public: { resourcesUrl },
  } = useRuntimeConfig()

  if (_scene) {
    useEnvironment(_scene)
    useRoom(_scene)
    scene = _scene
    scene.add(chairModel)
  }

  async function loadModel(
    geometry: string,
    materialName: string,
    isBasePart: boolean,
    groupId?: string
  ) {
    // TODO: Remove
    if (geometry === `${resourcesUrl}null`) return new Group()

    if (models[geometry] && models[geometry].model) {
      models[geometry].materialName = materialName
      models[geometry].isActive = true
      return models[geometry].model.clone()
    }
    const gltf = await gltfLoader.loadAsync(geometry)
    const model = gltf.scene

    const points = model.getObjectByName('points')
    const pointsClone = points?.clone()

    let name = 'base'

    if (points) {
      points.children.forEach((point) => {
        if (point instanceof Mesh) {
          const material = point.material
          const partName = material.name.split('_')
          // if (!point.userData.point_name) return
          // const partName = point.userData.point_name.split('_')
          if (partName.length && partName[0] !== 'base') {
            name = partName[0]
          }
          point.visible = false
        }
      })
    }

    models[geometry] = {
      model,
      points: pointsClone,
      isBasePart,
      name,
      materialName,
      groupId,
      isActive: true,
    }

    return model.clone()
  }

  async function loadChildren(objects: Objects, groupId?: string) {
    const chairModelTmp = new Group()

    await Promise.all(
      objects.map(async (object) => {
        if (isSceneObject(object)) {
          await loadModel(
            resourcesUrl + object.geometry,
            object.materialName,
            object.isBasePart,
            groupId
          )
        } else {
          const id = nanoid()
          await loadChildren(object.children, id)
        }
      })
    )

    return chairModelTmp
  }

  function addMaterial(model: Group, materialName: string) {
    model.traverse((obj) => {
      if (obj instanceof Mesh) {
        obj.castShadow = true
        if (materialName && materialName.length && materials[materialName]) {
          obj.material = materials[materialName]
        }
      }
    })
  }

  function moveToPosition(model: Group, point: Mesh, accumulator: Vector3) {
    model.position.copy(accumulator)
    model.rotation.copy(point.rotation)
    model.scale.copy(point.scale)
  }

  function addFromPoints(
    name: string,
    points: Object3D<Object3DEventMap>,
    accumulator: Vector3
  ) {
    const chairModelTmp = new Group()

    points.children.forEach((point) => {
      if (point instanceof Mesh) {
        const material = point.material
        const partName = material.name.split('_')

        console.log(material.name)

        // if (!point.userData.point_name) return
        // const partName = point.userData.point_name.split('_')

        if (partName[1] === name) {
          return
        }

        const part = Object.values(models).find(
          (model) => model.name === partName[1] && model.isActive
        )

        if (part && part.isActive) {
          const partModel = part.model.clone()

          addMaterial(partModel, part.materialName)

          const newAccumulator = point.position.clone().add(accumulator)

          if (part.groupId) {
            Object.values(models).forEach((model) => {
              if (
                model.groupId === part.groupId &&
                model.name !== partName[1]
              ) {
                const partRelatedModel = model.model.clone()
                addMaterial(partRelatedModel, model.materialName)

                moveToPosition(partRelatedModel, point, newAccumulator)
                chairModelTmp.add(partRelatedModel)
              }
            })
          }

          moveToPosition(partModel, point, newAccumulator)
          chairModelTmp.add(partModel)

          const points = part.model.getObjectByName('points')

          if (points) {
            chairModelTmp.add(
              addFromPoints(partName[0], points, newAccumulator)
            )
          }
        }
      }
    })

    return chairModelTmp
  }

  function addToScene() {
    const chairModelTmp = new Group()

    for (const modelPath in models) {
      const modelData = models[modelPath]

      if (!modelData.isBasePart || !modelData.isActive) {
        continue
      }

      const model = modelData.model.clone()

      model.traverse((obj) => {
        if (obj instanceof Mesh) {
          obj.castShadow = true
          obj.receiveShadow = true
          if (
            modelData.materialName &&
            modelData.materialName.length &&
            materials[modelData.materialName]
          ) {
            obj.material = materials[modelData.materialName]
            obj.material.side = DoubleSide
          }
        }
      })

      const points = modelData.model.getObjectByName('points')

      if (points) {
        chairModelTmp.add(
          addFromPoints(modelData.name, points, new Vector3(0, 0, 0))
        )
      }

      chairModelTmp.add(model)
    }

    return chairModelTmp
  }

  async function loadConfiguration(configuration: Configuration3d) {
    console.log(configuration)

    const arcusObjects = [
      {
        children: [
          {
            geometry: '/models/arcus/rm60-kolecka.glb',
            materialName: 'plastic',
          },
          {
            geometry: '/models/arcus/rm60-ram.glb',
            materialName: 'plastic',
          },
          {
            geometry: '/models/arcus/rm60-disk.glb',
            materialName: 'plastic',
          },
        ],
      },
      {
        children: [
          {
            geometry: '/models/arcus/arcus-kriz-pist.glb',
            materialName: 'plastic',
          },
          {
            geometry: '/models/arcus/arcus-kriz.glb',
            materialName: 'plastic',
          },
        ],
      },
      {
        geometry: '/models/arcus/arcus-mechanika.glb',
        materialName: 'plastic',
      },
      {
        geometry: '/models/arcus/arcus-operadlo.glb',
        materialName: 'D8033',
      },
      {
        geometry: '/models/arcus/arcus-operadlo-ram.glb',
        materialName: 'plastic',
      },
      {
        geometry: '/models/arcus/arcus-pist.glb',
        materialName: 'plastic',
      },
      {
        children: [
          {
            geometry: '/models/arcus/arcus-podruc-telo.glb',
            materialName: 'plastic',
          },
          {
            geometry: '/models/arcus/arcus-podruc-ram.glb',
            materialName: 'plastic',
          },
          {
            geometry: '/models/arcus/arcus-podruc-operadlo.glb',
            materialName: 'plastic light',
          },
        ],
      },
      {
        geometry: '/models/arcus/arcus-sedak.glb',
        materialName: 'D8033',
      },
      {
        geometry: '/models/arcus/arcus-kryt.glb',
        materialName: 'plastic',
      },
      {
        children: [
          {
            geometry: '/models/arcus/arcus-hloperka.glb',
            materialName: 'D8033',
          },
          {
            geometry: '/models/arcus/arcus-hloperka-ram.glb',
            materialName: 'plastic',
          },
        ],
      },
      {
        geometry: '/models/arcus/arcus-bedoperka.glb',
        materialName: 'plastic light',
      },
    ]
    const alvaObjects = [
      {
        geometry: '/models/alva/alva-330-sya-mechanika.glb',
        materialName: 'plastic',
      },
      {
        geometry: '/models/kolecko.glb',
        materialName: 'plastic',
      },
      {
        children: [
          {
            geometry: '/models/podrucram.glb',
            materialName: 'plastic',
          },
          {
            geometry: '/models/podruc1.glb',
            materialName: 'plastic',
          },
          {
            geometry: '/models/podruc2.glb',
            materialName: 'plastic',
          },
          {
            geometry: '/models/podruc3.glb',
            materialName: 'D8033',
          },
        ],
      },
      {
        geometry: '/models/alva/alva-330-sya-kryt.glb',
        materialName: 'plastic',
      },
      {
        geometry: '/models/alva/alva-330-sya-operadlo.glb',
        materialName: 'D8033',
      },
      {
        geometry: '/models/alva/alva-330-sya-operadloram.glb',
        materialName: 'plastic',
      },
      {
        geometry: '/models/alva/alva-330-sya-pist.glb',
        materialName: 'plastic',
      },
      {
        geometry: '/models/f40-n1-kriz.glb',
        materialName: 'plastic',
      },

      {
        geometry: '/models/alva/alva-330-sya-sedak.glb',
        materialName: 'D8033',
      },
    ]

    // configuration = {
    //   objects: arcusObjects, // arcusObjects, alvaObjects
    //   materials: [
    //     {
    //       metadata: {
    //         version: 4.6,
    //         type: 'Material',
    //         generator: 'Material.toJSON',
    //       },
    //       uuid: '402ed4a5-cc24-0b54-efb4-8f466820e25f',
    //       type: 'MeshStandardMaterial',
    //       color: 16777215,
    //       roughness: 1,
    //       metalness: 0,
    //       emissive: 0,
    //       map: '7acaac1f-11ad-ff5f-1f01-24ef94de67c5',
    //       envMapIntensity: 1,
    //       blendColor: 0,
    //       name: 'D8033',
    //       textures: [
    //         {
    //           uuid: '7acaac1f-11ad-ff5f-1f01-24ef94de67c5',
    //           name: '',
    //           image: '9ef392dd-111c-06d5-697b-25c4a2efeb45',
    //           mapping: 300,
    //           channel: 0,
    //           repeat: [2, 2],
    //           offset: [0, 0],
    //           center: [0, 0],
    //           rotation: 0,
    //           wrap: [1000, 1000],
    //           format: 1023,
    //           internalFormat: null,
    //           type: 1009,
    //           colorSpace: 'srgb',
    //           minFilter: 1008,
    //           magFilter: 1006,
    //           anisotropy: 1,
    //           flipY: true,
    //           generateMipmaps: true,
    //           premultiplyAlpha: false,
    //           unpackAlignment: 4,
    //         },
    //       ],
    //       images: [
    //         {
    //           uuid: '9ef392dd-111c-06d5-697b-25c4a2efeb45',
    //           url: '/file/nodes/fabric/image/e79.jpg/320x320/FIT',
    //         },
    //       ],
    //     },
    //     {
    //       metadata: {
    //         version: 4.6,
    //         type: 'Material',
    //         generator: 'Material.toJSON',
    //       },
    //       uuid: '13c0a6f2-b3af-4712-baa5-cb7db538ee35',
    //       type: 'MeshStandardMaterial',
    //       color: 0,
    //       roughness: 0.5,
    //       metalness: 0,
    //       emissive: 0,
    //       map: null,
    //       envMapIntensity: 1,
    //       blendColor: 0,
    //       name: 'plastic',
    //       textures: [],
    //       images: [],
    //     },
    //     {
    //       metadata: {
    //         version: 4.6,
    //         type: 'Material',
    //         generator: 'Material.toJSON',
    //       },
    //       uuid: '13c0a6f2-b3af-4712-baa5-cb7db538ee35',
    //       type: 'MeshStandardMaterial',
    //       color: 6184542,
    //       roughness: 0.5,
    //       metalness: 0,
    //       emissive: 0,
    //       map: null,
    //       envMapIntensity: 1,
    //       blendColor: 0,
    //       name: 'plastic light',
    //       textures: [],
    //       images: [],
    //     },
    //   ],
    // }

    Object.values(models).forEach((model) => {
      model.isActive = false
    })

    const newMaterials = configuration.materials.filter(
      (material) => materials[material?.name] === undefined
    )

    const images = await objectLoader.parseImagesAsync(
      newMaterials
        .map((material) => material?.images)
        .flat(2)
        .filter((value) => value !== undefined)
        .map((value) => ({
          ...value,
          url: `${resourcesUrl}${value.url}/320x320/FIT`,
        }))
    )

    const textures = objectLoader.parseTextures(
      newMaterials
        .map((material) => material?.textures)
        .flat(Infinity)
        .filter((value) => value !== undefined),
      images
    )

    materialLoader.setTextures(textures)

    newMaterials.forEach((material: Materials) => {
      materials[material.name] = materialLoader.parse(material)
    })

    // Load models
    // Load points
    // Get base parts
    // Connect accessories to base parts recursively (accessories can have other accessories)

    await loadChildren(configuration.objects)

    const chairModelTmp = addToScene()

    chairModelTmp.traverse((obj) => {
      obj.receiveShadow = true
      obj.castShadow = true
    })

    scene.add(chairModelTmp)
    scene.remove(chairModel)
    chairModel = chairModelTmp
  }

  return {
    loadConfiguration,
    chairModel,
    models,
  }
}
